import { InjectionToken } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  StateSignals,
  withMethods,
  withState,
  WritableStateSource,
} from '@ngrx/signals';
import { IQChat, IQChatFeedback } from '../classes/iqBoost';

/** A conversation listed in an IQ Boost v2 dialog's sidebar; backed by a LangGraph thread. */
export interface IQBoostConversation {
  threadId: string;
  title: string;
  /**
   * ISO time of the last question asked on it, which orders the list. Where the server does not
   * report it, the thread's last update.
   */
  updatedAt: string;
  /** ISO time the user pinned it; set only while it is pinned, and orders the pinned ones. */
  pinnedAt?: string;
}

export type IQBoostConversationState = {
  /** LangGraph thread holding the server-side conversation; null until one is created. */
  threadId: string | null;
  /** User questions and AI answers, in order. */
  chatHistory: IQChat[];
  /** The user's conversations, pinned or not, the one with the latest question first. */
  conversations: IQBoostConversation[];
};

const initialState: IQBoostConversationState = {
  threadId: null,
  chatHistory: [],
  conversations: [],
};

const conversationMethods = (
  store: StateSignals<IQBoostConversationState> & WritableStateSource<IQBoostConversationState>,
) => ({
  setThreadId(threadId: string | null): void {
    patchState(store, () => ({ threadId }));
  },
  appendMessage(message: IQChat): void {
    patchState(store, (state) => ({ chatHistory: [...state.chatHistory, message] }));
  },
  /** Records the reader's vote on one message of the open chat, by its position. */
  rateMessage(index: number, feedback: IQChatFeedback): void {
    patchState(store, (state) => ({
      chatHistory: state.chatHistory.map((message, position) =>
        position === index ? { ...message, feedback } : message,
      ),
    }));
  },
  /** Leaves the open chat; the list of past conversations stays. */
  startNewConversation(): void {
    patchState(store, () => ({ threadId: null, chatHistory: [] }));
  },
  openConversation(threadId: string, chatHistory: IQChat[]): void {
    patchState(store, () => ({ threadId, chatHistory }));
  },
  setConversations(conversations: IQBoostConversation[]): void {
    patchState(store, () => ({ conversations }));
  },
  /** Lists a new conversation first, replacing any entry for the same thread. */
  addConversation(conversation: IQBoostConversation): void {
    patchState(store, (state) => ({
      conversations: [
        conversation,
        ...state.conversations.filter((item) => item.threadId !== conversation.threadId),
      ],
    }));
  },
  /** Moves the conversation first, as the server orders a thread that was just updated. */
  touchConversation(threadId: string, updatedAt = new Date().toISOString()): void {
    patchState(store, (state) => {
      const conversation = state.conversations.find((item) => item.threadId === threadId);
      return conversation
        ? {
            conversations: [
              { ...conversation, updatedAt },
              ...state.conversations.filter((item) => item.threadId !== threadId),
            ],
          }
        : {};
    });
  },
  renameConversation(threadId: string, title: string): void {
    patchState(store, (state) => ({
      conversations: state.conversations.map((item) =>
        item.threadId === threadId ? { ...item, title } : item,
      ),
    }));
  },
  /** Pins the conversation at `pinnedAt`, an ISO time, or unpins it with null; the order stays. */
  setConversationPin(threadId: string, pinnedAt: string | null): void {
    patchState(store, (state) => ({
      conversations: state.conversations.map((item) =>
        item.threadId === threadId ? { ...item, pinnedAt: pinnedAt ?? undefined } : item,
      ),
    }));
  },
  /** Drops the conversation from the list, and leaves it when it is the open one. */
  removeConversation(threadId: string): void {
    patchState(store, (state) => ({
      conversations: state.conversations.filter((item) => item.threadId !== threadId),
      ...(state.threadId === threadId ? { threadId: null, chatHistory: [] } : {}),
    }));
  },
});

// Each dialog's store is built on this, so one dialog's conversations never show in the other.
export function withIQBoostConversation() {
  return signalStoreFeature(withState(initialState), withMethods(conversationMethods));
}

/** Either dialog's store, as the chat shared by the dialogs reads and changes it. */
export type IQBoostConversationStore = StateSignals<IQBoostConversationState> &
  ReturnType<typeof conversationMethods>;

/**
 * The store of the dialog the chat belongs to. Each IQ Boost v2 dialog provides its own - with
 * `useExisting`, since the stores outlive the dialogs.
 */
export const IQBOOST_CONVERSATION_STORE = new InjectionToken<IQBoostConversationStore>(
  'IQBOOST_CONVERSATION_STORE',
);
